import './styles.css';
import type { RenderStatusResponse, UploadRole } from '../../shared/types';
import { PairUploader } from './uploader/PairUploader';
import { createFileCard, type FileCardHandle } from './components/FileCard';
import { formatBytes, formatDuration } from './utils/format';

const messageInput = document.getElementById('message-input') as HTMLTextAreaElement;
const template = document.getElementById('file-card-template') as HTMLTemplateElement;
const resumeBanner = document.getElementById('resume-banner') as HTMLDivElement;
const resumeBannerText = resumeBanner.querySelector('span') as HTMLSpanElement;
const resumeBannerDismiss = document.getElementById('resume-banner-dismiss') as HTMLButtonElement;
const pairStatus = document.getElementById('pair-status') as HTMLDivElement;

const ROLES: UploadRole[] = ['video', 'subtitle'];

const slotEls: Record<UploadRole, { dropzone: HTMLDivElement; input: HTMLInputElement; list: HTMLUListElement }> = {
  video: {
    dropzone: document.getElementById('dropzone-video') as HTMLDivElement,
    input: document.getElementById('file-input-video') as HTMLInputElement,
    list: document.getElementById('file-list-video') as HTMLUListElement,
  },
  subtitle: {
    dropzone: document.getElementById('dropzone-subtitle') as HTMLDivElement,
    input: document.getElementById('file-input-subtitle') as HTMLInputElement,
    list: document.getElementById('file-list-subtitle') as HTMLUListElement,
  },
};

const uploader = new PairUploader(() => render());
const cards: Record<UploadRole, FileCardHandle | null> = { video: null, subtitle: null };

function showSlotError(role: UploadRole, text: string): void {
  const { dropzone } = slotEls[role];
  let el = dropzone.parentElement?.querySelector<HTMLParagraphElement>('.slot-error');
  if (!el) {
    el = document.createElement('p');
    el.className = 'slot-error';
    el.style.color = 'var(--color-error)';
    el.style.fontSize = '0.8rem';
    el.style.margin = '-4px 0 0';
    dropzone.after(el);
  }
  el.textContent = text;
  window.clearTimeout(Number(el.dataset.timer));
  const timer = window.setTimeout(() => el?.remove(), 6000);
  el.dataset.timer = String(timer);
}

const RENDER_STAGE_LABELS: Record<RenderStatusResponse['status'], string> = {
  starting: 'Both files uploaded. Starting video render…',
  queued: 'Queued for rendering…',
  processing: 'Rendering your video…',
  completed: '✓ Render complete!',
  failed: 'The render failed.',
};

function appendText(parent: HTMLElement, text: string, opts: { bold?: boolean; small?: boolean } = {}): HTMLParagraphElement {
  const p = document.createElement('p');
  p.style.margin = '0';
  if (opts.bold) p.style.fontWeight = '600';
  if (opts.small) p.style.fontSize = '0.82rem';
  p.textContent = text;
  parent.appendChild(p);
  return p;
}

function appendButton(parent: HTMLElement, label: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'link-button';
  button.textContent = label;
  button.addEventListener('click', onClick);
  parent.appendChild(button);
  return button;
}

function renderRenderStatus(): void {
  const status = uploader.getRenderStatus();
  if (!status) {
    pairStatus.hidden = true;
    return;
  }

  pairStatus.hidden = false;
  pairStatus.innerHTML = '';
  pairStatus.classList.toggle('pair-status-warning', status.status === 'failed');

  appendText(pairStatus, RENDER_STAGE_LABELS[status.status], { bold: true });

  if (status.status === 'processing' && typeof status.progress === 'number') {
    const track = document.createElement('div');
    track.className = 'progress-track';
    track.style.width = '100%';
    const fill = document.createElement('div');
    fill.className = 'progress-fill';
    fill.style.width = `${Math.min(100, Math.max(0, status.progress)).toFixed(1)}%`;
    track.appendChild(fill);
    pairStatus.appendChild(track);
    appendText(pairStatus, status.stage ? `${Math.round(status.progress)}% · ${status.stage}` : `${Math.round(status.progress)}%`, {
      small: true,
    });
  } else if (status.status === 'processing' && status.stage) {
    appendText(pairStatus, status.stage, { small: true });
  }

  if (status.status === 'completed') {
    if (status.durationSec) {
      appendText(pairStatus, `Rendered in ${formatDuration(status.durationSec)}.`, { small: true });
    }
    if (status.outputUrl) {
      const link = document.createElement('a');
      link.href = status.outputUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'browse-button';
      link.style.textDecoration = 'none';
      link.style.display = 'inline-block';
      link.textContent = 'Download video';
      pairStatus.appendChild(link);
    }
    if (status.warnings && status.warnings.length > 0) {
      appendText(pairStatus, `Warnings: ${status.warnings.join('; ')}`, { small: true });
    }
  }

  if (status.status === 'failed') {
    if (status.errorMessage) appendText(pairStatus, status.errorMessage, { small: true });
    appendButton(pairStatus, 'Retry render', () => uploader.retryRender());
  }

  // Starting a new pair abandons visibility into an in-progress render (it
  // keeps running server-side regardless, just no longer shown here) — only
  // offer that once this one has actually finished, so it isn't lost by
  // accident.
  if (status.status === 'completed' || status.status === 'failed') {
    appendButton(pairStatus, 'Upload another pair', () => uploader.startNewPair());
  }
}

function render(): void {
  for (const role of ROLES) {
    const task = uploader.getTask(role);
    const list = slotEls[role].list;

    if (!task) {
      if (cards[role]) {
        cards[role]!.element.remove();
        cards[role] = null;
      }
      continue;
    }

    let handle = cards[role];
    if (!handle || !list.contains(handle.element)) {
      handle = createFileCard(
        task,
        {
          onPause: () => uploader.pause(role),
          onResume: () => uploader.resume(role),
          onCancel: () => void uploader.remove(role),
          onRemove: () => void uploader.remove(role),
        },
        template,
      );
      cards[role] = handle;
      list.innerHTML = '';
      list.appendChild(handle.element);
    }
    handle.update(task.getSnapshot());
  }

  renderRenderStatus();
}

function handleFiles(role: UploadRole, files: FileList | File[]): void {
  const file = files[0];
  if (!file) return;
  uploader.setMessage(messageInput.value.trim());
  const { error } = uploader.setFile(role, file);
  if (error) showSlotError(role, error);
  resumeBanner.hidden = true;
}

for (const role of ROLES) {
  const { dropzone, input } = slotEls[role];

  dropzone.addEventListener('click', () => input.click());
  dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      input.click();
    }
  });

  input.addEventListener('change', () => {
    if (input.files && input.files.length > 0) handleFiles(role, input.files);
    input.value = '';
  });

  let dragCounter = 0;
  dropzone.addEventListener('dragenter', (e) => {
    e.preventDefault();
    dragCounter++;
    dropzone.classList.add('drag-active');
  });
  dropzone.addEventListener('dragover', (e) => e.preventDefault());
  dropzone.addEventListener('dragleave', () => {
    dragCounter = Math.max(0, dragCounter - 1);
    if (dragCounter === 0) dropzone.classList.remove('drag-active');
  });
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dragCounter = 0;
    dropzone.classList.remove('drag-active');
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) handleFiles(role, e.dataTransfer.files);
  });

  const browseButton = dropzone.querySelector<HTMLButtonElement>(`[data-browse-for="${role}"]`);
  browseButton?.addEventListener('click', (e) => {
    e.stopPropagation();
    input.click();
  });
}

resumeBannerDismiss.addEventListener('click', () => {
  resumeBanner.hidden = true;
});

async function bootstrap(): Promise<void> {
  try {
    await uploader.init();
  } catch {
    for (const role of ROLES) showSlotError(role, 'Could not reach the server. Please refresh the page and try again.');
    return;
  }

  const unfinished = uploader.unfinishedSessionSummaries();
  if (unfinished.length > 0) {
    const summary = unfinished
      .slice(0, 3)
      .map((s) => `${s.filename} (${formatBytes(s.fileSize)})`)
      .join(', ');
    resumeBannerText.textContent = `You have an unfinished upload: ${summary}. Select the same file into the matching slot to resume.`;
    resumeBanner.hidden = false;
  }
}

void bootstrap();
